import { formatRelative, formatTime, type StatusLevel } from '@westhetics/ui'
import type { DocumentStatus } from '@/features/documents/api'
import type {
  AutentiqueEvent,
  AutentiqueIgnoredReason,
  JobHeartbeat,
  JobName,
  JobStatus,
  OperationAreaFilter,
  OperationsPeriod,
} from './api'

/**
 * As frases do monitoramento (docs/areas/administracao.md, "Monitoramento"; RN-ADM-94 a 99): o período,
 * as rotinas, as áreas, os avisos da Autentique e os tamanhos. Status sempre por extenso.
 */

export const PERIOD_LABELS: Record<OperationsPeriod, string> = {
  day: '24 horas',
  week: '7 dias',
}

/** "Nenhuma falha nas últimas 24 horas" · "42 falhas nos últimos 7 dias". */
export const PERIOD_PHRASES: Record<OperationsPeriod, string> = {
  day: 'nas últimas 24 horas',
  week: 'nos últimos 7 dias',
}

export const AREA_LABELS: Record<OperationAreaFilter, string> = {
  all: 'Todas',
  jobs: 'Rotinas',
  sends: 'Envios',
  autentique: 'Autentique',
  pdf: 'PDF',
  email: 'E-mail',
  integrations: 'Integrações',
  api: 'API',
}

/** A área dentro de uma frase: "Nenhuma falha de PDF…". */
export const AREA_IN_SENTENCE: Record<OperationAreaFilter, string> = {
  all: '',
  jobs: 'das rotinas',
  sends: 'de envio',
  autentique: 'da Autentique',
  pdf: 'de PDF',
  email: 'de e-mail',
  integrations: 'das integrações',
  api: 'da API',
}

export const JOB_LABELS: Record<JobName, string> = {
  signatures: 'Assinaturas',
  photos: 'Fotos dos planos',
  integrations: 'Integrações',
}

/** O que cada rotina faz, na linha de baixo. */
export const JOB_NOTES: Record<JobName, string> = {
  signatures: 'envios, lembretes, vencimentos, avisos e reconciliação da Autentique',
  photos: 'vence as sessões de QR e as fotos que não chegaram',
  integrations: 'entregas de webhook aos sistemas das clínicas',
}

export const JOB_STATUS: Record<JobStatus, { label: string; level: StatusLevel }> = {
  on_time: { label: 'Em dia', level: 'done' },
  late: { label: 'Atrasada', level: 'attention' },
  failed: { label: 'Falhou', level: 'critical' },
  never: { label: 'Nunca rodou', level: 'attention' },
}

const SECOND = 1000
const MINUTE = 60 * SECOND

/** "há 40 segundos" abaixo de um minuto; depois, o relativo de sempre ("há 3 minutos"). */
export function ago(iso: string, now: number = Date.now()): string {
  const diff = now - new Date(iso).getTime()
  if (diff < 5 * SECOND) return 'agora'
  if (diff < MINUTE) return `há ${Math.floor(diff / SECOND)} segundos`
  return formatRelative(new Date(iso), new Date(now))
}

/** "atualizado há 20 segundos", ao lado do título. */
export function updatedLabel(generatedAt: string, now: number = Date.now()): string {
  const when = ago(generatedAt, now)
  return when === 'agora' ? 'atualizado agora' : `atualizado ${when}`
}

/** A linha da rotina: quando rodou e, se falhou, a causa. */
export function jobRunText(job: JobHeartbeat, now: number = Date.now()): string {
  if (!job.lastRunAt) return 'nunca rodou neste ambiente'
  const run = `última rodada ${ago(job.lastRunAt, now)}`
  return job.status === 'failed' && job.lastError ? `${run} · ${job.lastError}` : run
}

/** "1 coisa pede atenção" · "3 coisas pedem atenção". */
export function attentionTitle(total: number): string {
  return total === 1 ? '1 coisa pede atenção' : `${total} coisas pedem atenção`
}

/** "Nenhuma falha nas últimas 24 horas" · "Nenhuma falha de PDF nos últimos 7 dias". */
export function noFailureTitle(area: OperationAreaFilter, period: OperationsPeriod): string {
  const of = AREA_IN_SENTENCE[area]
  return `Nenhuma falha${of ? ` ${of}` : ''} ${PERIOD_PHRASES[period]}`
}

/** Os tipos de aviso que a Autentique manda e a Westhetics usa (`_shared/autentique/webhook.ts`). */
export const AUTENTIQUE_EVENT_LABELS: Record<string, string> = {
  'document.created': 'Documento criado',
  'document.updated': 'Documento atualizado',
  'document.finished': 'Documento concluído',
  'document.deleted': 'Documento excluído',
  'signature.created': 'Signatário incluído',
  'signature.updated': 'Signatário atualizado',
  'signature.deleted': 'Signatário removido',
  'signature.viewed': 'Documento visualizado',
  'signature.accepted': 'Documento assinado',
  'signature.rejected': 'Assinatura recusada',
  'signature.delivery_failed': 'O link não chegou',
  'signature.biometric_approved': 'Biometria aprovada',
  'signature.biometric_unapproved': 'Biometria reprovada',
}

/** O tipo por extenso; o desconhecido aparece como veio, e o sem tipo diz isso. */
export function autentiqueEventLabel(type: string | null): string {
  if (!type) return 'Aviso sem tipo'
  return AUTENTIQUE_EVENT_LABELS[type] ?? type
}

export const IGNORED_REASON_LABELS: Record<AutentiqueIgnoredReason, string> = {
  unhandled_type: 'aviso que a Westhetics não usa',
  no_target: 'nenhum envio com esta referência',
  attempts_exhausted: 'esgotou as tentativas',
  canceled_in_provider: 'o documento não existe mais na Autentique',
}

/** O resultado do aviso: a etiqueta e, quando há, a causa embaixo. */
export function eventResult(event: AutentiqueEvent): {
  label: string
  level: StatusLevel
  note: string | null
} {
  if (event.state === 'applied') return { label: 'Aplicado', level: 'done', note: null }
  if (event.state === 'pending') {
    return event.attempts > 0
      ? {
          label: 'Em nova tentativa',
          level: 'attention',
          note: event.lastError
            ? `${event.attempts}ª tentativa · ${event.lastError}`
            : `${event.attempts}ª tentativa`,
        }
      : { label: 'Aguardando', level: 'waiting', note: null }
  }
  if (event.ignoredReason === 'attempts_exhausted') {
    return { label: 'Esgotou as tentativas', level: 'critical', note: event.lastError }
  }
  return {
    label: 'Ignorado',
    level: 'waiting',
    note: event.ignoredReason ? IGNORED_REASON_LABELS[event.ignoredReason] : null,
  }
}

export const DOCUMENT_STATUS_LABELS: Record<DocumentStatus, string> = {
  generated: 'Gerado',
  awaiting_patient: 'Aguardando paciente',
  awaiting_clinic_signature: 'Aguardando a clínica',
  signed: 'Assinado',
  not_signed: 'Não assinado',
  expired: 'Link vencido',
  canceled: 'Cancelado',
  deleted: 'Excluído',
}

/** "WX-2041-AB · Assinado"; sem envio, nada. */
export function documentText(event: AutentiqueEvent): string | null {
  const status = event.documentStatus ? DOCUMENT_STATUS_LABELS[event.documentStatus] : null
  const parts = [event.protocolNumber, status].filter(Boolean)
  return parts.length ? parts.join(' · ') : null
}

const UNITS = ['KB', 'MB', 'GB', 'TB'] as const

/** "1,2 GB" · "850 MB" · "512 bytes". Base 1024, uma casa, vírgula. */
export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} ${bytes === 1 ? 'byte' : 'bytes'}`
  let value = bytes / 1024
  let unit = 0
  while (value >= 1024 && unit < UNITS.length - 1) {
    value /= 1024
    unit += 1
  }
  const text = value.toLocaleString('pt-BR', { maximumFractionDigits: value < 10 ? 1 : 0 })
  return `${text} ${UNITS[unit]}`
}

/** "12 vezes desde 14:02": só quando a falha se repetiu. */
export function occurrencesText(occurrences: number, firstOccurredAt: string): string | null {
  if (occurrences <= 1) return null
  return `${occurrences} vezes desde ${formatTime(new Date(firstOccurredAt))}`
}
